/**
 * Restoration Diagnose — reads an AudioBuffer and reports what is MEASURABLY
 * wrong with it, and which source preset that evidence supports.
 *
 * Output JSON (consumed by audio-restoration.blade.php → diagnostic card):
 *   {
 *     channels, sampleRate, duration,        // basic shape
 *     noiseFloorDb,                           // 10th-percentile 30-ms frame RMS
 *     clicksPerSec,                           // isolated spikes per second of QUIET audio
 *     crackle: { quiet10, quiet20, all10 },   // the click evidence, see _clicks
 *     mainsHz,                                // {hz: 50|60, rel: dB under the programme, lines} — hum-detect.js; null if none
 *     hfKneeHz,                               // PROGRAMME bandwidth, see _programmeBandwidthHz
 *     dcOffset,                               // mean of samples
 *     lufs,                                   // EBU R128 if loudness-r128.js loaded
 *     clippedPct, peakDb,                     // overload: share of samples in a flat top
 *     stereoCorr,                             // L/R correlation, null on mono input
 *     programmeCorr,                          // the same over the loud frames only (is the SOURCE mono?)
 *     content,                                // 'speech' | 'music' | null — what the caller's detector said
 *     suggestedPreset,                        // 'vinyl-33-45'|'vinyl-78'|'voice-archive'|'custom'
 *     severity: { noise, clicks, hum, hf, clip }   // 'ok'|'warn'|'bad' per meter; noise also 'na'
 *   }
 *
 * ── Rebuilt 2026-10-14 (support chat 686) ─────────────────────────────────
 * The first version suggested «Vinyl 78» to 194 of 235 uploads in 30 days
 * (83 %), and the page applies the suggestion: mono fold, rumble cut at 100 Hz,
 * −8 dB of air, the 78 de-emphasis curve, heavy declick — on LP rips, on
 * cassettes, on modern masters, on a phone call. Two of its meters measured
 * nothing:
 *
 *   clicks  — «median + 6 × MAD» of the first difference, where the MAD was the
 *             median itself read twice. Any programme crosses seven times its
 *             own median hundreds of times a second: 91 «clicks»/s on clean
 *             netlabel masters, 312 on audiobooks, 223 on real shellac —
 *             no separation at all.
 *   HF knee — a single-bin Goertzel at 48 log-spaced frequencies of ONE 8-second
 *             window: the level of one bin in 384 000 is noise, and the «knee»
 *             landed at 1.5–2.5 kHz for every class of recording.
 *
 * Measured on real transfers from archive.org (48 shellac 78s of the George
 * Blood collection, 22 raw LP transfers, 30 modern masters, 40 speech and 44
 * live-music recordings; 30 s of each, FLAC where the item has it):
 *
 *   A click is 1–3 samples; a drum hit or a consonant keeps the signal busy
 *   for milliseconds. So a spike counts only when it stands 10× above the
 *   LOCAL level of the second difference and the signal 24–48 samples either
 *   side is back at that level. And it is counted in the QUIET fifth of the
 *   recording — pauses and decays — where clean programme has nothing
 *   impulsive and a worn disc crackles exactly as everywhere else:
 *
 *                       spikes ≥10× /s of quiet     spikes ≥20×
 *     shellac 78, FLAC  1.8 … 58.8 … 131 (p10/med/p90)   9.4 median
 *     raw LP, FLAC      3.5 … 47.1 … 113                 21.4
 *     modern masters    0   …  0.2 …   7                  0 (p90 0.5)
 *     speech            0   …  0.4–1.2 … 14               0 (p90 0.8)
 *     live music        0   …  0–1.7 … 22 (applause)      0 (p90 6.3)
 *
 *   The band of the PROGRAMME is where the loud frames stop rising above the
 *   quiet ones: surface noise fills a transfer to 20 kHz, the music under it
 *   does not (shellac: 5.7–7.1 kHz median; LP 10–20; modern 22).
 *
 * The rule that follows: a preset is suggested only on POSITIVE evidence.
 * Clicks → a vinyl preset, «78» only when the source is mono, its programme
 * stops below 8 kHz AND the crackle is heavy; speech (the detector of the page) → the voice preset;
 * anything else → «custom», which switches nothing on. Hiss alone is not
 * told from quiet music by any meter here, so no tape preset is guessed:
 * the person names a cassette or a reel themselves — one tap on its chip.
 *
 * The NOISE meter gives a verdict only for speech. Its number is the level of
 * the quietest tenth of the recording: between the phrases of a speaker that is
 * the floor of the room or of the line, in music it is the music — clean modern
 * masters read −21…−28 dBFS («strong noise» for 27 of 30), raw LP transfers
 * with real surface noise −35…−42 (3 of 22). No level statistic separated the
 * two on this corpus (the gap to the median, a «wall» in the level histogram,
 * the flatness of the quiet spectrum — all overlap), so for anything that is
 * not speech the severity is 'na': the number is shown, no verdict is claimed.
 *
 * Not the click detector of mix-readiness.js, and not by oversight: that one
 * answers «are there EDIT clicks in a finished master» (rare, loud, k = 50 with
 * an audibility gate) and on the same transfers reads ≥ 6 clicks/min on 12 of
 * 30 modern masters and on 24 of 48 shellac sides; this one answers «does the
 * MEDIUM crackle» (dense, small, under the programme) — 0 of 30 and 34 of 48.
 *
 * Runs in ~60 ms on three 10-second windows from 25 %, 50 % and 75 % of the
 * file — never its first seconds (a lead-in groove, an announcement) alone.
 */
(function () {
    'use strict';

    var CLICK_K = 10, CLICK_K_STRONG = 20;
    /* The thresholds sit between the classes measured above. */
    var CLICKY_STRONG_PER_S = 1.5;      // ≥20× spikes per second of quiet audio
    var CLICKY_WEAK_PER_S = 15;         // ≥10× spikes per second of quiet audio
    var HEAVY_PER_S = 20;
    var MONO_CORR = 0.85;
    var NARROW_HZ = 8000;
    /* One step of 16-bit audio. The local scale is never taken below it: in
       digital silence (a gate, a synthetic voice, the gap between two files)
       the median is zero and the first sample of anything would be a «spike»
       of any ratio. Analogue transfers never get near it — 1e-4 already cost
       one clean LP of 22, this value none. */
    var SCALE_FLOOR = 3e-5;

    /** Up to three mono windows of `seconds` from 25 / 50 / 75 % of the buffer
     *  (the whole buffer when it is shorter than their sum). */
    function _windows(buffer, seconds) {
        var sr = buffer.sampleRate, len = buffer.length, ch = buffer.numberOfChannels;
        var win = Math.floor(seconds * sr), plan = [];
        if (len <= win * 3) plan.push([0, len]);
        else for (var k = 1; k <= 3; k++) { var c = Math.floor(len * k / 4); plan.push([c - (win >> 1), c - (win >> 1) + win]); }
        var mono = plan.map(function (p) {
            var n = p[1] - p[0], out = new Float32Array(n);
            for (var c2 = 0; c2 < ch; c2++) {
                var data = buffer.getChannelData(c2);
                for (var i = 0; i < n; i++) out[i] += data[p[0] + i];
            }
            if (ch > 1) for (var j = 0; j < n; j++) out[j] /= ch;
            return out;
        });
        mono.plan = plan;
        return mono;
    }

    /**
     * L/R correlation of the PROGRAMME — the loud two fifths of the frames.
     * A mono disc read by a stereo cartridge carries the same music in both
     * channels and private surface noise in each: over the whole file the
     * noise drags the correlation down (0.3–0.9 on real shellac transfers),
     * in the loud frames the music decides.
     */
    function _programmeCorrelation(buffer, windows, frames, frame) {
        if (buffer.numberOfChannels < 2) return null;
        var a = buffer.getChannelData(0), b = buffer.getChannelData(1);
        var sab = 0, saa = 0, sbb = 0;
        for (var f = Math.floor(frames.length * 0.60); f < frames.length; f++) {
            var base = windows.plan[frames[f].w][0] + frames[f].off;
            for (var i = 0; i < frame; i += 2) {
                var x = a[base + i], y = b[base + i];
                sab += x * y; saa += x * x; sbb += y * y;
            }
        }
        var den = Math.sqrt(saa * sbb);
        return den > 1e-12 ? Math.round((sab / den) * 1000) / 1000 : null;
    }

    /**
     * Level of every frame of every window, pooled: [{w, off, rms}] sorted
     * quiet → loud. The level is the MEDIAN of sixteen sub-block powers, not
     * the mean: «quiet» has to describe the programme, and a pop of two
     * samples at 0.9 lifts the mean of its own frame by 20 dB — the frame left
     * the quiet fifth and the pop was not counted exactly where it is most
     * audible (corpus: raw LP transfers 7 → 8 of 8 found, nothing gained by
     * the clean classes).
     */
    function _frames(windows, frame) {
        var all = [], sub = frame >> 4, e = new Float64Array(16);
        for (var w = 0; w < windows.length; w++) {
            var x = windows[w];
            for (var o = 0; o + frame <= x.length; o += frame) {
                for (var b = 0; b < 16; b++) {
                    var s = 0, base = o + b * sub;
                    for (var i = 0; i < sub; i++) s += x[base + i] * x[base + i];
                    e[b] = s / sub;
                }
                e.sort();
                all.push({ w: w, off: o, rms: Math.sqrt((e[7] + e[8]) / 2) });
            }
        }
        all.sort(function (a, b) { return a.rms - b.rms; });
        return all;
    }

    /**
     * Noise floor via 10th-percentile of short-frame RMS values. Short frames
     * isolate "the quietest 10% of the recording" which is reliably the noise
     * background even on music with dynamic range.
     */
    function _noiseFloorDb(windows, sr) {
        var frame = Math.floor(0.030 * sr), hop = Math.floor(0.015 * sr), rms = [];
        for (var w = 0; w < windows.length; w++) {
            var x = windows[w];
            for (var i = 0; i + frame < x.length; i += hop) {
                var sum = 0;
                for (var j = 0; j < frame; j++) sum += x[i + j] * x[i + j];
                rms.push(Math.sqrt(sum / frame));
            }
        }
        if (!rms.length) return -100;
        rms.sort(function (a, b) { return a - b; });
        return 20 * Math.log10(Math.max(1e-7, rms[Math.floor(rms.length * 0.10)]));
    }

    /**
     * Isolated spikes of the second difference, per second: over everything
     * (all10) and over the quiet fifth of the frames (quiet10 at ≥10× the local
     * level, quiet20 at ≥20×). Each window is scanned on its own — the join of
     * two windows is a step, and a step is a spike.
     *
     * Tried and dropped, on the same transfers: vetoing «evenly spaced» spikes
     * as a waveform (glottal pulses, a raw sawtooth) — dense crackle piles its
     * intervals up against the 2 ms refractory and reads as «even»: raw LPs
     * found fell from 16 to 10 of 22, for 3 % of false alarms it could save.
     */
    function _clicks(windows, sr, frames, frame) {
        var W = Math.max(64, Math.round(0.010 * sr)), refr = Math.round(0.002 * sr);
        var quietN = Math.max(4, Math.floor(frames.length * 0.20));
        var quiet = windows.map(function (x) { return new Uint8Array(Math.ceil(x.length / frame) + 1); });
        for (var q = 0; q < quietN && q < frames.length; q++) quiet[frames[q].w][Math.floor(frames[q].off / frame)] = 1;
        var all10 = 0, q10 = 0, q20 = 0, total = 0, quietSamples = 0;
        var tmp = new Float32Array(64), sorted = new Float32Array(64);
        for (var w = 0; w < windows.length; w++) {
            var x = windows[w], n = x.length;
            if (n < W * 3) continue;
            var d2 = new Float32Array(n);
            for (var i = 2; i < n; i++) { var v = x[i] - 2 * x[i - 1] + x[i - 2]; d2[i] = v < 0 ? -v : v; }
            var last10 = -1e9, last20 = -1e9;
            total += n;
            for (var c = W; c + W < n; c += W) {
                for (var j = 0; j < 64; j++) tmp[j] = d2[c - W + Math.floor(j * 2 * W / 64)];
                sorted.set(tmp); sorted.sort();
                var med = Math.max(sorted[32], SCALE_FLOOR);
                var isQuiet = quiet[w][Math.floor(c / frame)] === 1;
                if (isQuiet) quietSamples += W;
                for (var s = c; s < c + W; s++) {
                    var r = d2[s] / med;
                    if (r < CLICK_K || s < 64 || s + 64 >= n) continue;
                    // around the spike: the programme itself must be calm 24–48 samples away
                    var side = 0;
                    for (var k = 24; k <= 48; k += 4) { if (d2[s - k] > side) side = d2[s - k]; if (d2[s + k] > side) side = d2[s + k]; }
                    if (side > med * 4) continue;                 // an onset, not a click
                    if (s - last10 > refr) { all10++; if (isQuiet) q10++; last10 = s; }
                    if (r >= CLICK_K_STRONG && s - last20 > refr) { if (isQuiet) q20++; last20 = s; }
                }
            }
        }
        var sec = Math.max(0.5, total / sr), qsec = Math.max(0.5, quietSamples / sr);
        return {
            all10: Math.round(all10 / sec * 10) / 10,
            quiet10: Math.round(q10 / qsec * 10) / 10,
            quiet20: Math.round(q20 / qsec * 10) / 10,
        };
    }

    /* ── averaged spectra ─────────────────────────────────────────────── */
    var _N = 2048, _hann = null, _re = null, _im = null;
    function _fft(re, im) {
        var n = re.length;
        for (var i = 1, j = 0; i < n; i++) { var bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit; if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; } }
        for (var size = 2; size <= n; size <<= 1) {
            var half = size >> 1, ang = -2 * Math.PI / size, wr = Math.cos(ang), wi = Math.sin(ang);
            for (var k = 0; k < n; k += size) {
                var cr = 1, ci = 0;
                for (var m = 0; m < half; m++) {
                    var or = re[k + m + half] * cr - im[k + m + half] * ci, oi = re[k + m + half] * ci + im[k + m + half] * cr;
                    re[k + m + half] = re[k + m] - or; im[k + m + half] = im[k + m] - oi; re[k + m] += or; im[k + m] += oi;
                    var tw = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = tw;
                }
            }
        }
    }
    /** Mean power spectrum of the listed frames (each `_N` samples from its offset). */
    function _spectrum(windows, list) {
        if (!_hann) { _hann = new Float64Array(_N); for (var h = 0; h < _N; h++) _hann[h] = 0.5 - 0.5 * Math.cos(2 * Math.PI * h / _N); _re = new Float64Array(_N); _im = new Float64Array(_N); }
        var acc = new Float64Array(_N / 2 + 1), count = 0;
        for (var f = 0; f < list.length; f++) {
            var x = windows[list[f].w], off = list[f].off;
            if (off + _N > x.length) continue;
            for (var i = 0; i < _N; i++) { _re[i] = x[off + i] * _hann[i]; _im[i] = 0; }
            _fft(_re, _im);
            for (var k = 0; k <= _N / 2; k++) acc[k] += _re[k] * _re[k] + _im[k] * _im[k];
            count++;
        }
        if (count) for (var q = 0; q <= _N / 2; q++) acc[q] /= count;
        return count ? acc : null;
    }
    function _bandDb(psd, sr, f0, f1) {
        var a = Math.max(1, Math.round(f0 * _N / sr)), b = Math.min(_N / 2, Math.round(f1 * _N / sr)), s = 0, c = 0;
        for (var k = a; k <= b; k++) { s += psd[k]; c++; }
        return 10 * Math.log10(Math.max(s / Math.max(1, c), 1e-20));
    }

    /**
     * Programme bandwidth — the highest sixth-octave band in which the loud
     * frames stand at least 6 dB above the quiet ones. Bands:
     *   < 5 kHz   → shellac / 78 / a telephone line
     *   5–10 kHz  → AM, worn tape, band-limited
     *   > 10 kHz  → vinyl LP / reel / modern
     * With no quiet frames to compare against (a wall of sound), the band is
     * not known and the answer is the Nyquist edge: «not narrow».
     */
    function _programmeBandwidthHz(windows, sr, frames) {
        var top = sr / 2 - 300;
        if (frames.length < 20) return Math.round(top);
        var quiet = _spectrum(windows, frames.slice(0, Math.max(4, Math.floor(frames.length * 0.10))));
        var loud = _spectrum(windows, frames.slice(Math.floor(frames.length * 0.60)));
        if (!quiet || !loud) return Math.round(top);
        var bw = 0, step = Math.pow(2, 1 / 6), half = Math.pow(2, 1 / 12);
        for (var f = 1000; f < top; f *= step) {
            if (_bandDb(loud, sr, f / half, f * half) - _bandDb(quiet, sr, f / half, f * half) >= 6) bw = f;
        }
        return Math.round(bw || top);
    }

    /**
     * Mains hum — asked of the shared detector, hum-detect.js (HumDetect, the
     * owner since 2026-09-24: narrow lines at k·50 or k·60 Hz present the whole
     * time, with a chance level from non-mains fundamentals). The meter that
     * stood here compared ONE Goertzel bin with the broadband level of the
     * quiet frames: planted hum at −35 dBFS was found in 0 of 58 real
     * recordings. HumDetect on the same set: 57 of 58 at −35 dBFS, 49 at −45,
     * 43 at −55, the frequency right in all but a few; 0 of 10 on pink noise.
     * `rel` is the hum against the programme, dB — what decides «audible».
     * No detector on the page → no claim.
     */
    function _mainsHum(mono, sr, content, HD) {
        if (!HD || typeof HD.analyze !== 'function') return null;
        var r;
        try { r = HD.analyze(mono, sr, { content: content || null }); } catch (e) { return null; }
        if (!r || !r.family) return null;
        return { hz: r.family, rel: r.humRel == null ? null : Math.round(r.humRel), lines: r.lines ? r.lines.length : 0 };
    }

    function _dcOffset(windows) {
        var s = 0, n = 0;
        for (var w = 0; w < windows.length; w++) {
            var x = windows[w], step = Math.max(1, Math.floor(x.length / 4096));
            for (var i = 0; i < x.length; i += step) { s += x[i]; n++; }
        }
        return s / Math.max(1, n);
    }

    function _lufs(buffer) {
        if (window.LoudnessR128 && typeof window.LoudnessR128.measureIntegratedLUFS === 'function') {
            try { return window.LoudnessR128.measureIntegratedLUFS(buffer); }
            catch (e) { return null; }
        }
        return null;
    }

    /** What the evidence supports. No evidence → 'custom': nothing is switched on. */
    function _suggestPreset(d) {
        var c = d.crackle;
        var clicky = c.quiet20 >= CLICKY_STRONG_PER_S || c.quiet10 >= CLICKY_WEAK_PER_S;
        var heavy = c.quiet10 >= HEAVY_PER_S;
        // Speech first: a voice recording with a few stray clicks is still a
        // voice recording. Only a heavily crackling one is a disc before it is a voice.
        if (d.content === 'speech' && !heavy) return 'voice-archive';
        if (clicky) {
            // «78» adds a mono fold, a 100 Hz rumble cut, the 78 de-emphasis curve and
            // −8 dB of air to the LP preset: right for shellac, wrong for a worn mono
            // LP. So it takes all three signs — a mono source, a programme that stops
            // below 8 kHz and heavy crackle; a clicky disc short of that gets the LP
            // preset, which only removes what was measured.
            var mono = d.channels < 2 || (d.programmeCorr !== null && d.programmeCorr >= MONO_CORR);
            return (mono && heavy && d.hfKneeHz < NARROW_HZ) ? 'vinyl-78' : 'vinyl-33-45';
        }
        return 'custom';
    }

    /**
     * Clipping — share of samples sitting inside a flat-topped plateau.
     *
     * Deliberately scanned over the WHOLE file, not the windows the other
     * metrics use: overload is an event, not a property. A tape that only
     * slams the converter during the chorus would read perfectly clean from a
     * mid-file window, and the one defect the user can actually hear would go
     * unmentioned. A strided pass keeps this affordable.
     *
     * The run-length test is what separates a clip from a peak: a single
     * sample touching the ceiling is normal programme material, three or more
     * consecutive samples pinned at the same value is a converter that ran out
     * of numbers. Mirrors the detector in restoration-declip.js so the
     * diagnosis and the repair cannot disagree about what counts.
     */
    function _clipping(buffer) {
        var chCount = buffer.numberOfChannels;
        var peak = 0, c, d, i;
        for (c = 0; c < chCount; c++) {
            d = buffer.getChannelData(c);
            var stride = Math.max(1, Math.floor(d.length / 2000000));
            for (i = 0; i < d.length; i += stride) {
                var a = d[i] < 0 ? -d[i] : d[i];
                if (a > peak) peak = a;
            }
        }
        if (peak < 0.5) return { pct: 0, peakDb: peak > 0 ? Math.round(20 * Math.log10(peak) * 10) / 10 : -99 };

        var thresh = peak * 0.98;
        var clipped = 0;
        var examined = 0;
        for (c = 0; c < chCount; c++) {
            d = buffer.getChannelData(c);
            i = 0;
            while (i < d.length) {
                var v = d[i];
                if ((v < 0 ? -v : v) < thresh) { i++; examined++; continue; }
                var sign = v >= 0 ? 1 : -1;
                var j = i;
                while (j < d.length && d[j] * sign >= thresh) j++;
                var runLen = j - i;
                if (runLen >= 3) clipped += runLen;
                examined += runLen;
                i = j;
            }
        }
        return {
            pct: Math.round((clipped / Math.max(1, examined)) * 10000) / 100,
            peakDb: Math.round(20 * Math.log10(peak) * 10) / 10,
        };
    }

    /**
     * L/R correlation — tells the UI whether folding to mono is free noise
     * reduction (a mono disc read by a stereo cartridge: same music, private
     * scratches per wall) or an image-destroying mistake (real stereo).
     */
    function _stereoCorrelation(buffer) {
        if (buffer.numberOfChannels < 2) return null;
        var a = buffer.getChannelData(0);
        var b = buffer.getChannelData(1);
        var n = Math.min(a.length, b.length);
        var stride = Math.max(1, Math.floor(n / 400000));
        var sab = 0, saa = 0, sbb = 0;
        for (var i = 0; i < n; i += stride) {
            var x = a[i], y = b[i];
            sab += x * y; saa += x * x; sbb += y * y;
        }
        var den = Math.sqrt(saa * sbb);
        return den > 1e-12 ? Math.round((sab / den) * 1000) / 1000 : 0;
    }

    function _severity(d) {
        var c = d.crackle;
        var clicky = c.quiet20 >= CLICKY_STRONG_PER_S || c.quiet10 >= CLICKY_WEAK_PER_S;
        return {
            // a verdict only where the quiet tenth IS the floor — between the phrases of a speaker
            noise:  d.content !== 'speech' ? 'na' : (d.noiseFloorDb > -32 ? 'bad' : (d.noiseFloorDb > -45 ? 'warn' : 'ok')),
            clicks: (c.quiet10 >= HEAVY_PER_S || c.quiet20 >= 5) ? 'bad' : (clicky ? 'warn' : 'ok'),
            // audible is what counts: a line 50 dB under the programme is real and inaudible
            hum:    !d.mainsHz ? 'ok' : (d.mainsHz.rel == null || d.mainsHz.rel >= -30 ? 'bad' : (d.mainsHz.rel >= -45 ? 'warn' : 'ok')),
            hf:     d.hfKneeHz < 5000 ? 'bad' : (d.hfKneeHz < 10000 ? 'warn' : 'ok'),
            // 0.05 % of a 3-minute file is still ~400 pinned samples — audible
            // as grit on every peak. Anything past half a percent is gross.
            clip:   d.clippedPct > 0.5 ? 'bad' : (d.clippedPct > 0.02 ? 'warn' : 'ok'),
        };
    }

    /**
     * @param buffer AudioBuffer (or anything with its four members)
     * @param opts   { content: 'speech' | 'music' | null } — the verdict of the
     *               page's content detector, when it has one
     */
    function diagnose(buffer, opts) {
        var sr = buffer.sampleRate;
        var windows = _windows(buffer, 10);
        var FRAME = 2048;
        var frames = _frames(windows, FRAME);

        var noiseFloorDb = _noiseFloorDb(windows, sr);
        var crackle      = _clicks(windows, sr, frames, FRAME);
        var HD = (opts && opts.HumDetect) || (typeof window !== 'undefined' && window.HumDetect) || null;
        var mainsHz      = _mainsHum(windows[windows.length >> 1], sr, opts && opts.content, HD);
        var hfKneeHz     = _programmeBandwidthHz(windows, sr, frames);
        var dcOffset     = _dcOffset(windows);
        var lufs         = _lufs(buffer);
        var clip         = _clipping(buffer);
        var stereoCorr   = _stereoCorrelation(buffer);
        var programmeCorr = _programmeCorrelation(buffer, windows, frames, FRAME);

        var result = {
            channels: buffer.numberOfChannels,
            sampleRate: sr,
            duration: buffer.duration,
            noiseFloorDb: Math.round(noiseFloorDb * 10) / 10,
            clicksPerSec: crackle.quiet10,
            crackle: crackle,
            mainsHz: mainsHz,
            hfKneeHz: hfKneeHz,
            dcOffset: Math.round(dcOffset * 10000) / 10000,
            lufs: lufs == null ? null : Math.round(lufs * 10) / 10,
            clippedPct: clip.pct,
            peakDb: clip.peakDb,
            stereoCorr: stereoCorr,
            programmeCorr: programmeCorr,
            content: (opts && opts.content) || null,
        };
        result.suggestedPreset = _suggestPreset(result);
        result.severity = _severity(result);
        return result;
    }

    window.RestorationDiagnose = { diagnose: diagnose };
})();
